import React, { useState, useEffect, useRef } from 'react';
import { getOdooAttributes, getOdooValueAttributes } from '../../hooks/api/odoo';

const VariantModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  variantData, 
  onVariantDataChange, 
  variantImagePreview, 
  onImageUpload, // Nuevo prop para el ID de la empresa
  existingVariants = [], // Nuevo prop para variantes existentes
  packageDimensions = { anchoPaq: '', largoPaq: '', altoPaq: '' } // Dimensiones del paquete
}) => {
  const [odooAttributes, setOdooAttributes] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState('');
  const [attributeValues, setAttributeValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showOdooSection, setShowOdooSection] = useState(true);
  const [showAttributeForm, setShowAttributeForm] = useState(true);
  const [variants, setVariants] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [attributeSearchText, setAttributeSearchText] = useState(''); // Búsqueda de atributos
  const [attributeSearchTimeout, setAttributeSearchTimeout] = useState(null); // Timeout para búsqueda de atributos
  const [isSearchingAttributes, setIsSearchingAttributes] = useState(false); // Estado de búsqueda de atributos
  const [isAttributeDropdownOpen, setIsAttributeDropdownOpen] = useState(false); // Estado para controlar el dropdown de atributos
  const attributeDropdownRef = useRef(null); // Ref para el dropdown de atributos
  const [currentVariant, setCurrentVariant] = useState({
    odooAttributes: [], // Array de atributos de Odoo
    color: '',
    talla: '',
    peso: '',
    volumen: '',
    sku: '',
    barcode: '', // Campo para código de barras
    mainImage: null, // Imagen principal
    additionalImages: [] // Array de imágenes adicionales
  });
  const [showAdditionalImages, setShowAdditionalImages] = useState(false);
  const [requiredAttributes, setRequiredAttributes] = useState([]); // Atributos obligatorios
  const [isInputFocused, setIsInputFocused] = useState(false); // Estado para controlar si el input está enfocado
  const [focusedRequiredInput, setFocusedRequiredInput] = useState(null); // Estado para controlar qué input de atributo obligatorio está enfocado
  const [isVolumeManuallyEdited, setIsVolumeManuallyEdited] = useState(false); // Estado para rastrear si el volumen fue editado manualmente
  const [globalAttributes, setGlobalAttributes] = useState([]); // Atributos globales que tendrán todas las variantes
  const [focusedAttributeInput, setFocusedAttributeInput] = useState(null); // Atributo cuyo input está enfocado
  const [attributeSearchTexts, setAttributeSearchTexts] = useState({}); // Texto de búsqueda por atributo
  const [attributeValuesMap, setAttributeValuesMap] = useState({}); // Valores por atributo
  const [editingVariantId, setEditingVariantId] = useState(null); // ID de la variante que se está editando
  const [isAttributesSectionCollapsed, setIsAttributesSectionCollapsed] = useState(false); // Estado para controlar si la sección de atributos está colapsada
  const [attributesError, setAttributesError] = useState(null); // Estado para errores al cargar atributos
  
  // Función para limpiar el estado de focus cuando se selecciona un valor
  const clearFocusState = () => {
    setIsInputFocused(false);
    setFocusedRequiredInput(null);
    setSearchText(''); // Limpiar el texto de búsqueda
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
  };
  const entId = "5f1378dcef2f752aeda16673";

  // Cargar atributos de Odoo y variantes existentes cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      loadOdooAttributes();
      setShowAdditionalImages(false); // Limpiar el checkbox cuando se abre el modal
      setIsVolumeManuallyEdited(false); // Resetear el flag de edición manual al abrir el modal
      
      // Cargar variantes existentes si las hay
      if (existingVariants && existingVariants.length > 0) {
        console.log('Cargando variantes existentes:', existingVariants);
        
        // Preservar las imágenes en las variantes existentes
        const variantsWithPreservedImages = existingVariants.map(variant => ({
          ...variant,
          mainImagePreview: variant.image || variant.mainImagePreview || '',
          additionalImagesPreview: variant.additionalImages || variant.additionalImagesPreview || []
        }));
        
        setVariants(variantsWithPreservedImages);
        
        // Si hay variantes, establecer los atributos de la primera como globales y obligatorios
        if (existingVariants.length > 0 && existingVariants[0].odooAttributes) {
          const firstVariantAttrs = existingVariants[0].odooAttributes;
          // Crear atributos globales desde los atributos de la primera variante
          const globalAttrs = firstVariantAttrs.map(attr => ({
            attributeId: attr.attributeId,
            attributeName: attr.attributeName
          }));
          setGlobalAttributes(globalAttrs);
          setRequiredAttributes(firstVariantAttrs.map(attr => attr.attributeId));
        }
      } else {
        // Si no hay variantes existentes, limpiar el estado
        setVariants([]);
        setRequiredAttributes([]);
        setGlobalAttributes([]);
      }
    }
  }, [isOpen, existingVariants]);

  // Cargar valores del atributo seleccionado
  useEffect(() => {
    if (selectedAttribute) {
      loadAttributeValues();
    }
  }, [selectedAttribute]);

  // Cargar valores de atributos obligatorios cuando hay variantes previas
  useEffect(() => {
    if (variants.length > 0 && requiredAttributes.length > 0 && isOpen) {
      // Cargar valores para el primer atributo obligatorio por defecto
      if (requiredAttributes[0]) {
        loadAttributeValuesForRequired(requiredAttributes[0]);
      }
    }
  }, [variants.length, requiredAttributes, isOpen]);

  // Limpiar timeouts cuando el componente se desmonte
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      if (attributeSearchTimeout) {
        clearTimeout(attributeSearchTimeout);
      }
    };
  }, [searchTimeout, attributeSearchTimeout]);

  // Cerrar dropdown cuando se hace click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (attributeDropdownRef.current && !attributeDropdownRef.current.contains(event.target)) {
        setIsAttributeDropdownOpen(false);
      }
    };

    if (isAttributeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAttributeDropdownOpen]);

  // Calcular volumen automáticamente cuando cambien las dimensiones del paquete
  useEffect(() => {
    if (packageDimensions && isOpen && !isVolumeManuallyEdited) {
      const anchoPaq = packageDimensions.anchoPaq || '';
      const largoPaq = packageDimensions.largoPaq || '';
      const altoPaq = packageDimensions.altoPaq || '';
      
      const ancho = anchoPaq === '' ? 0 : parseFloat(anchoPaq) || 0;
      const largo = largoPaq === '' ? 0 : parseFloat(largoPaq) || 0;
      const alto = altoPaq === '' ? 0 : parseFloat(altoPaq) || 0;
      
      // Calcular volumen automáticamente si todos los valores son mayores que 0
      if (ancho > 0 && largo > 0 && alto > 0) {
        const volumen = ancho * largo * alto;
        setCurrentVariant(prev => ({ ...prev, volumen: volumen.toFixed(2) }));
      } else {
        // Si alguna dimensión está vacía o es 0, limpiar el volumen
        setCurrentVariant(prev => {
          if (!prev.volumen || prev.volumen === '') {
            return { ...prev, volumen: '' };
          }
          return prev;
        });
      }
    }
  }, [packageDimensions, isOpen, isVolumeManuallyEdited]);

  // Función para guardar cambios de la variante actual
  const saveCurrentVariant = () => {
    // Validar que todos los atributos globales tengan un valor seleccionado
    const missingAttributes = globalAttributes.filter(globalAttr => 
      !currentVariant.odooAttributes.some(attr => attr.attributeId === globalAttr.attributeId)
    );
    
    if (missingAttributes.length > 0) {
      alert(`Debes seleccionar un valor para todos los atributos: ${missingAttributes.map(a => a.attributeName).join(', ')}`);
      return;
    }
    
    if (editingVariantId) {
      // Actualizar variante existente
      const updatedVariant = {
        ...currentVariant,
        id: editingVariantId,
        mainImagePreview: currentVariant.mainImage,
        additionalImagesPreview: currentVariant.additionalImages,
        image: currentVariant.mainImage || currentVariant.image || '',
        additionalImages: currentVariant.additionalImages || currentVariant.additionalImages || []
      };
      setVariants(variants.map(v => v.id === editingVariantId ? updatedVariant : v));
      setEditingVariantId(null);
    } else {
      // Crear nueva variante
      const newVariant = {
        ...currentVariant,
        id: Date.now(), // ID temporal
        mainImagePreview: currentVariant.mainImage,
        additionalImagesPreview: currentVariant.additionalImages,
        // Preservar imágenes existentes si no hay nuevas
        image: currentVariant.mainImage || currentVariant.image || '',
        additionalImages: currentVariant.additionalImages || currentVariant.additionalImages || []
      };
      setVariants([...variants, newVariant]);
      
      // Si es la primera variante, establecer los atributos globales como obligatorios
      if (variants.length === 0) {
        setRequiredAttributes(globalAttributes.map(attr => attr.attributeId));
      }
    }
    
    // Limpiar formulario actual pero mantener los atributos globales
    setCurrentVariant({
      odooAttributes: [],
      color: '',
      talla: '',
      peso: '',
      volumen: '',
      sku: '',
      barcode: '',
      mainImage: null,
      additionalImages: []
    });
    setSelectedAttribute('');
    setAttributeValues([]);
    setSearchText(''); // Limpiar el texto de búsqueda
    setShowOdooSection(true); // Mantener la sección abierta
    setShowAttributeForm(false); // Cerrar el formulario
    setShowAdditionalImages(false); // Limpiar el checkbox de imágenes adicionales
    setIsVolumeManuallyEdited(false); // Resetear el flag de edición manual
    setFocusedAttributeInput(null);
    setAttributeSearchTexts({});
  };

  // Función para eliminar variante
  const removeVariant = (variantId) => {
    setVariants(variants.filter(v => v.id !== variantId));
  };

  // Función para editar una variante
  const editVariant = async (variantId) => {
    const variantToEdit = variants.find(v => v.id === variantId);
    if (variantToEdit) {
      setEditingVariantId(variantId);
      setCurrentVariant({
        odooAttributes: variantToEdit.odooAttributes || [],
        color: variantToEdit.color || '',
        talla: variantToEdit.talla || '',
        peso: variantToEdit.peso || '',
        volumen: variantToEdit.volumen || '',
        sku: variantToEdit.sku || '',
        barcode: variantToEdit.barcode || '',
        mainImage: variantToEdit.mainImage || variantToEdit.mainImagePreview || variantToEdit.image || null,
        additionalImages: variantToEdit.additionalImages || variantToEdit.additionalImagesPreview || []
      });
      setShowOdooSection(true);
      setShowAdditionalImages(variantToEdit.additionalImages && variantToEdit.additionalImages.length > 0);
      
      // Cargar valores de atributos para cada atributo seleccionado
      if (variantToEdit.odooAttributes && variantToEdit.odooAttributes.length > 0) {
        const newAttributeValuesMap = { ...attributeValuesMap };
        for (const attr of variantToEdit.odooAttributes) {
          if (attr.attributeId && !newAttributeValuesMap[attr.attributeId]) {
            try {
              const response = await getOdooValueAttributes(entId, attr.attributeId, '');
              const values = response?.attributeValues || [];
              newAttributeValuesMap[attr.attributeId] = values;
            } catch (error) {
              console.error(`Error loading values for attribute ${attr.attributeId}:`, error);
              newAttributeValuesMap[attr.attributeId] = [];
            }
          }
        }
        setAttributeValuesMap(newAttributeValuesMap);
      }
    }
  };

  // Función para cancelar edición
  const cancelEdit = () => {
    setEditingVariantId(null);
    setCurrentVariant({
      odooAttributes: [],
      color: '',
      talla: '',
      peso: '',
      volumen: '',
      sku: '',
      barcode: '',
      mainImage: null,
      additionalImages: []
    });
    setShowOdooSection(false);
    setShowAdditionalImages(false);
    setFocusedAttributeInput(null);
    setAttributeSearchTexts({});
  };

  // Función para actualizar variante actual
  const updateCurrentVariant = (field, value) => {
    setCurrentVariant(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Función para validar imagen
  const validateImage = (file) => {
    return new Promise((resolve, reject) => {
      // Validar tamaño del archivo (1MB = 1024 * 1024 bytes)
      if (file.size > 1024 * 1024) {
        reject('La imagen debe tener un tamaño máximo de 1MB');
        return;
      }

      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        reject('El archivo debe ser una imagen');
        return;
      }

      // Validar dimensiones
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        
        // Validar que sea cuadrada
        if (img.width !== img.height) {
          reject('La imagen debe ser cuadrada (mismo ancho y alto)');
          return;
        }
        
        // Validar dimensiones máximas
        if (img.width > 1500 || img.height > 1500) {
          reject('La imagen debe tener un tamaño máximo de 1500x1500 píxeles');
          return;
        }
        
        resolve(true);
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject('Error al cargar la imagen');
      };
      
      img.src = url;
    });
  };

  // Función para manejar carga de imagen principal
  const handleMainImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        await validateImage(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setCurrentVariant(prev => ({
            ...prev,
            mainImage: e.target.result
          }));
        };
        reader.readAsDataURL(file);
      } catch (error) {
        alert(`Error: ${error}`);
        // Limpiar el input de forma segura
        if (event.target && event.target.value !== undefined) {
          event.target.value = '';
        }
      }
    }
  };

  // Función para manejar carga de imágenes adicionales
  const handleAdditionalImagesUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    // Validar límite de 5 imágenes
    if (currentVariant.additionalImages.length + files.length > 5) {
      alert('No puedes cargar más de 5 imágenes adicionales en total');
      // Limpiar el input de forma segura
      if (event.target && event.target.value !== undefined) {
        event.target.value = '';
      }
      return;
    }

    for (const file of files) {
      try {
        await validateImage(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setCurrentVariant(prev => ({
            ...prev,
            additionalImages: [...prev.additionalImages, e.target.result]
          }));
        };
        reader.readAsDataURL(file);
      } catch (error) {
        alert(`Error con imagen ${file.name}: ${error}`);
      }
    }
    
    // Limpiar el input de forma segura
    if (event.target && event.target.value !== undefined) {
      event.target.value = '';
    }
  };

  // Función para eliminar imagen adicional
  const removeAdditionalImage = (index) => {
    setCurrentVariant(prev => ({
      ...prev,
      additionalImages: prev.additionalImages.filter((_, i) => i !== index)
    }));
  };

  // Función para agregar atributo de Odoo a la variante actual
  const addOdooAttribute = () => {
    if (selectedAttribute && currentVariant.odooAttributeValue) {
      const selectedAttr = odooAttributes.find(attr => attr.id.toString() === selectedAttribute);
      
      // Mejorar la búsqueda del valor seleccionado con múltiples estrategias
      let selectedValue = null;
      
      // Estrategia 1: Comparación directa de strings
      selectedValue = attributeValues.find(val => val.id.toString() === currentVariant.odooAttributeValue.toString());
      
      // Estrategia 2: Si no se encuentra, intentar comparación numérica
      if (!selectedValue) {
        selectedValue = attributeValues.find(val => Number(val.id) === Number(currentVariant.odooAttributeValue));
      }
      
      // Estrategia 3: Si aún no se encuentra, buscar por valor exacto
      if (!selectedValue) {
        selectedValue = attributeValues.find(val => val.id === currentVariant.odooAttributeValue);
      }
      
      // Debug logs para verificar la comparación
      console.log('Debug addOdooAttribute:');
      console.log('currentVariant.odooAttributeValue:', currentVariant.odooAttributeValue, 'type:', typeof currentVariant.odooAttributeValue);
      console.log('attributeValues:', attributeValues);
      console.log('selectedValue found:', selectedValue);
      
      if (!selectedValue) {
        console.error('No se pudo encontrar el valor seleccionado');
        alert('Error: No se pudo encontrar el valor seleccionado. Por favor, inténtalo de nuevo.');
        return;
      }
      
      const newAttribute = {
        attributeId: selectedAttribute,
        attributeName: selectedAttr?.name || '',
        valueId: currentVariant.odooAttributeValue,
        valueName: selectedValue.name || ''
      };

      console.log('newAttribute created:', newAttribute);

      setCurrentVariant(prev => ({
        ...prev,
        odooAttributes: [...prev.odooAttributes, newAttribute]
      }));

      // Limpiar selección actual pero mantener el formulario abierto
      setSelectedAttribute('');
      setCurrentVariant(prev => ({ ...prev, odooAttributeValue: '' }));
      setAttributeValues([]);
      setShowAttributeForm(true); // Mantener el formulario abierto
    }
  };

  // Función para eliminar atributo de Odoo de la variante actual
  const removeOdooAttribute = (index) => {
    setCurrentVariant(prev => ({
      ...prev,
      odooAttributes: prev.odooAttributes.filter((_, i) => i !== index)
    }));
  };

  // Función para agregar atributo global (que tendrán todas las variantes)
  const addGlobalAttribute = () => {
    if (selectedAttribute) {
      const selectedAttr = odooAttributes.find(attr => attr.id.toString() === selectedAttribute);
      
      if (selectedAttr && !globalAttributes.some(attr => attr.attributeId === selectedAttribute)) {
        const newGlobalAttribute = {
          attributeId: selectedAttribute,
          attributeName: selectedAttr.name || ''
        };
        
        setGlobalAttributes([...globalAttributes, newGlobalAttribute]);
        setSelectedAttribute('');
        setShowAttributeForm(false);
      }
    }
  };

  // Función para eliminar atributo global
  const removeGlobalAttribute = (attributeId) => {
    // Confirmar eliminación si hay variantes guardadas
    if (variants.length > 0) {
      const confirmDelete = window.confirm(
        `¿Estás seguro de eliminar este atributo? Se eliminará de todas las variantes guardadas (${variants.length} variante(s)).`
      );
      if (!confirmDelete) {
        return;
      }
    }
    
    setGlobalAttributes(globalAttributes.filter(attr => attr.attributeId !== attributeId));
    
    // Eliminar de la variante actual si existe
    setCurrentVariant(prev => ({
      ...prev,
      odooAttributes: prev.odooAttributes.filter(attr => attr.attributeId !== attributeId)
    }));
    
    // Eliminar de todas las variantes guardadas
    setVariants(prevVariants => 
      prevVariants.map(variant => ({
        ...variant,
        odooAttributes: variant.odooAttributes.filter(attr => attr.attributeId !== attributeId)
      }))
    );
    
    // Actualizar atributos requeridos si es necesario
    if (requiredAttributes.includes(attributeId)) {
      setRequiredAttributes(requiredAttributes.filter(id => id !== attributeId));
    }
  };

  const loadOdooAttributes = async (searchQuery = '') => {
    setLoading(true);
    setIsSearchingAttributes(!!searchQuery);
    setAttributesError(null); // Limpiar error anterior
    
    try {
      const response = await getOdooAttributes(entId, {
        limit: 10,
        offset: 0,
        searchText: searchQuery
      });
      
      // Validar que la respuesta sea válida
      if (!response) {
        throw new Error('No se recibió respuesta del servidor');
      }
      
      console.log(response,'attributes');
      // La respuesta tiene la estructura { attributes: [...] }
      const attributes = response?.attributes || [];
      setOdooAttributes(attributes);
      
      // Si no hay atributos y no es una búsqueda, puede ser un error
      if (attributes.length === 0 && !searchQuery) {
        setAttributesError('No se encontraron atributos disponibles. Por favor, intente más tarde.');
      }
    } catch (error) {
      console.error('Error cargando atributos de Odoo:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al cargar los atributos de Odoo. Por favor, intente nuevamente.';
      setAttributesError(errorMessage);
      setOdooAttributes([]);
      
      // Mostrar alerta al usuario solo si no es una búsqueda (para no interrumpir búsquedas)
      if (!searchQuery) {
        alert(`Error: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
      setIsSearchingAttributes(false);
    }
  };

  // Función de búsqueda de atributos con debounce
  const searchAttributes = (query) => {
    setAttributeSearchText(query);
    
    // Limpiar timeout anterior
    if (attributeSearchTimeout) {
      clearTimeout(attributeSearchTimeout);
    }
    
    // Si la búsqueda está vacía, cargar atributos iniciales inmediatamente
    if (!query || !query.trim()) {
      console.log('Búsqueda vacía, cargando atributos iniciales');
      loadOdooAttributes('');
      return;
    }
    
    // Aplicar debounce de 500ms para búsquedas
    const timeout = setTimeout(() => {
      console.log('Ejecutando búsqueda de atributos después de debounce');
      loadOdooAttributes(query);
    }, 500);
    
    setAttributeSearchTimeout(timeout);
  };

  const loadAttributeValues = async () => {
    try {
      const response = await getOdooValueAttributes(entId, selectedAttribute, '');
      console.log('loadAttributeValues response:', response);
      // La respuesta tiene la estructura { attributeValues: [...] }
      const values = response?.attributeValues || [];
      console.log('loadAttributeValues values:', values);
      setAttributeValues(values);
    } catch (error) {
      console.error('Error cargando valores del atributo:', error);
      setAttributeValues([]);
    }
  };

  const loadAttributeValuesWithSearch = async (searchTerm) => {
    try {
      const response = await getOdooValueAttributes(entId, selectedAttribute, searchTerm);
      console.log(response, 'attribute values with search');
      // La respuesta tiene la estructura { attributeValues: [...] }
      const values = response?.attributeValues || [];
      setAttributeValues(values);
    } catch (error) {
      console.error('Error cargando valores del atributo con búsqueda:', error);
      setAttributeValues([]);
    }
  };

  const loadAttributeValuesForRequired = async (attributeId) => {
    try {
      const response = await getOdooValueAttributes(entId, attributeId, '');
      console.log(response, 'required attribute values');
      // La respuesta tiene la estructura { attributeValues: [...] }
      const values = response?.attributeValues || [];
      setAttributeValues(values);
    } catch (error) {
      console.error('Error cargando valores del atributo obligatorio:', error);
      setAttributeValues([]);
    }
  };

  const loadAttributeValuesForRequiredWithSearch = async (attributeId, searchTerm) => {
    try {
      const response = await getOdooValueAttributes(entId, attributeId, searchTerm);
      console.log(response, 'required attribute values with search');
      // La respuesta tiene la estructura { attributeValues: [...] }
      const values = response?.attributeValues || [];
      setAttributeValues(values);
    } catch (error) {
      console.error('Error cargando valores del atributo obligatorio con búsqueda:', error);
      setAttributeValues([]);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        width: '90%',
        maxWidth: '850px',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {/* Header del Modal */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '18px 21px',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h6 style={{ margin: '0', fontWeight: 'bold', color: '##000000', fontSize: 20}}>
              Información del Producto
            </h6>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #ccc',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666'
            }}
          >
            ✕
          </button>
        </div>

        {/* Contenido del Modal */}
        <div style={{ padding: '20px' }}>
          {/* Sección de Agregar Atributos Globales - Al inicio del modal */}
          <div style={{ 
            backgroundColor: '#f8f9fa', 
            border: '2px solid #e9ecef', 
            borderRadius: '12px', 
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              marginBottom: '15px',
              paddingBottom: '10px',
              borderBottom: '2px solid #dee2e6',
              cursor: 'pointer'
            }}
            onClick={() => setIsAttributesSectionCollapsed(!isAttributesSectionCollapsed)}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ 
                  backgroundColor: '#007bff', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: '24px', 
                  height: '24px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  marginRight: '10px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  🏷️
                </span>
                <h6 style={{ margin: '0', fontWeight: 'bold', color: '#007bff', fontSize: '16px' }}>
                Atrubutos del producto
                </h6>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAttributesSectionCollapsed(!isAttributesSectionCollapsed);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: '#007bff',
                  padding: '4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {isAttributesSectionCollapsed ? '▼' : '▲'}
              </button>
            </div>

            {isAttributesSectionCollapsed ? (
              // Vista colapsada: solo mostrar atributos agregados
              <div style={{ 
                backgroundColor: 'white', 
                padding: '15px', 
                borderRadius: '10px',
                border: '1px solid #dee2e6',
                marginBottom: '20px'
              }}>
                {globalAttributes.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {globalAttributes.map((attr) => (
                      <div
                        key={attr.attributeId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          backgroundColor: '#e3f2fd',
                          border: '1px solid #bbdefb',
                          borderRadius: '6px',
                          fontSize: '13px'
                        }}
                      >
                        <span style={{ fontWeight: 'bold', color: '#1976d2' }}>
                          {attr.attributeName}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeGlobalAttribute(attr.attributeId);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#dc3545',
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '0',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ 
                    color: '#666', 
                    fontSize: '14px', 
                    fontStyle: 'italic',
                    textAlign: 'center',
                    padding: '10px'
                  }}>
                    No hay atributos agregados
                  </div>
                )}
              </div>
            ) : (
              // Vista expandida: mostrar todo el contenido
              <div style={{ 
                backgroundColor: 'white', 
                padding: '20px', 
                borderRadius: '10px',
                border: '1px solid #dee2e6',
                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '20px'
              }}>
                <h6 style={{ margin: '0 0 15px 0', color: '#000000', fontWeight: 'bold' }}>
                  Agregar Atributos (comunes para todas las variantes):
                </h6>
                
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1, position: 'relative' }} ref={attributeDropdownRef}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>
                      Seleccionar Atributo:
                    </label>
                    {/* Botón que simula el select */}
                    <div
                      onClick={() => setIsAttributeDropdownOpen(!isAttributeDropdownOpen)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        minHeight: '42px'
                      }}
                    >
                      <span style={{ color: selectedAttribute ? '#000' : '#999' }}>
                        {selectedAttribute 
                          ? odooAttributes.find(attr => attr.id.toString() === selectedAttribute)?.name || 'Seleccionar atributo de Odoo'
                          : 'Seleccionar atributo de Odoo'
                        }
                      </span>
                      <span style={{ fontSize: '12px', color: '#666' }}>▼</span>
                    </div>
                    
                    {/* Dropdown con búsqueda */}
                    {isAttributeDropdownOpen && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        marginTop: '4px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        maxHeight: '300px',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        {/* Campo de búsqueda dentro del dropdown */}
                        <div style={{
                          padding: '10px',
                          borderBottom: '1px solid #eee',
                          position: 'sticky',
                          top: 0,
                          backgroundColor: 'white',
                          zIndex: 1
                        }}>
                          <input
                            type="text"
                            placeholder="Buscar atributo por nombre..."
                            value={attributeSearchText}
                            onChange={(e) => {
                              e.stopPropagation();
                              searchAttributes(e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #ced4da',
                              borderRadius: '6px',
                              fontSize: '14px',
                              backgroundColor: 'white'
                            }}
                          />
                          {isSearchingAttributes && (
                            <div style={{ marginTop: '5px', fontSize: '11px', color: '#666', fontStyle: 'italic' }}>
                              Buscando...
                            </div>
                          )}
                        </div>
                        
                        {/* Lista de opciones */}
                        <div style={{
                          overflowY: 'auto',
                          maxHeight: '200px'
                        }}>
                          {loading && odooAttributes.length === 0 ? (
                            <div style={{ padding: '15px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
                              Cargando atributos...
                            </div>
                          ) : odooAttributes.length === 0 ? (
                            <div style={{ padding: '15px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
                              {attributeSearchText ? 'No se encontraron atributos' : 'No hay atributos disponibles'}
                            </div>
                          ) : (
                            odooAttributes
                              .filter(attr => !globalAttributes.some(globalAttr => globalAttr.attributeId === attr.id.toString()))
                              .map((attr) => (
                                <div
                                  key={attr._id}
                                  onClick={() => {
                                    setSelectedAttribute(attr.id.toString());
                                    setIsAttributeDropdownOpen(false);
                                    setAttributeSearchText(''); // Limpiar búsqueda al seleccionar
                                  }}
                                  style={{
                                    padding: '10px 15px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    fontSize: '14px',
                                    backgroundColor: selectedAttribute === attr.id.toString() ? '#e3f2fd' : 'white'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (selectedAttribute !== attr.id.toString()) {
                                      e.target.style.backgroundColor = '#f8f9fa';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (selectedAttribute !== attr.id.toString()) {
                                      e.target.style.backgroundColor = 'white';
                                    }
                                  }}
                                >
                                  {attr.name}
                                </div>
                              ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={addGlobalAttribute}
                    disabled={!selectedAttribute}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: selectedAttribute ? '#28a745' : '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: selectedAttribute ? 'pointer' : 'not-allowed',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Agregar
                  </button>
                </div>
                
                {/* Lista de atributos globales agregados */}
                {globalAttributes.length > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    <h6 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#666', fontWeight: 'bold' }}>
                      Atributos agregados:
                    </h6>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {globalAttributes.map((attr) => (
                        <div
                          key={attr.attributeId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 12px',
                            backgroundColor: '#e3f2fd',
                            border: '1px solid #bbdefb',
                            borderRadius: '6px',
                            fontSize: '13px'
                          }}
                        >
                          <span style={{ fontWeight: 'bold', color: '#1976d2' }}>
                            {attr.attributeName}
                          </span>
                          <button
                            onClick={() => removeGlobalAttribute(attr.attributeId)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#dc3545',
                              cursor: 'pointer',
                              fontSize: '16px',
                              padding: '0',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Botón para agregar nueva variante */}
          {!showOdooSection && (
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '20px',
              padding: '15px',
              border: '2px dashed #007bff',
              borderRadius: '8px',
              backgroundColor: '#f8f9ff'
            }}>
              <button
                onClick={() => setShowOdooSection(true)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  margin: '0 auto',
                  gap: '8px'
                }}
              >
                <span style={{ fontSize: '16px' }}>+</span>
                Agregar Variante
              </button>
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                Haz click para agregar una nueva variante con atributos de Odoo
              </p>
            </div>
          )}

          {/* Lista de variantes agregadas */}
          {variants.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h6 style={{ margin: '0 0 15px 0', fontWeight: 'bold', color: '#333' }}>
                Variantes Agregadas ({variants.length})
              </h6>
              {variants.map((variant, index) => (
                <div key={variant.id} style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '12px',
                  marginBottom: '10px',
                  backgroundColor: 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '4px' }}>
                      Variante {index + 1}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {variant.odooAttributes && variant.odooAttributes.length > 0 && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong>Atributos:</strong> {variant.odooAttributes.map(attr => 
                            `${attr.attributeName}: ${attr.valueName}`
                          ).join(' | ')}
                        </div>
                      )}
                      {variant.color && `Color: ${variant.color}`}
                      {variant.talla && ` | Talla: ${variant.talla}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => editVariant(variant.id)}
                      style={{
                        padding: '4px 8px', 
                        backgroundColor: '#007bff',
                        color: 'white', 
                        border: 'none',
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => removeVariant(variant.id)}
                      style={{
                        padding: '4px 8px', 
                        backgroundColor: '#dc3545',
                        color: 'white', 
                        border: 'none',
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Sección de Agregar Variante */}
          {showOdooSection && (
            <div style={{ 
              border: '1px solid #e0e0e0', 
              borderRadius: '8px', 
              marginBottom: '20px',
              backgroundColor: '#fafafa'
            }}>
              <div style={{
                padding: '15px',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#f8f9fa'
              }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <h6 style={{ margin: '0', fontWeight: 'bold', color: '#333' }}>
                    Registro de nueva variante
                  </h6>
                </div>
                <button
                  onClick={() => setShowOdooSection(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '18px',
                    color: '#666',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>
              
              {/* Contenido de configuración de variante */}
              <div style={{ padding: '20px' }}>
                {/* Sección 1: Atributos de Odoo */}
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  border: '2px solid #e9ecef', 
                  borderRadius: '12px', 
                  padding: '20px',
                  marginBottom: '25px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '15px',
                    paddingBottom: '10px',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    <span style={{ 
                      backgroundColor: '#007bff', 
                      color: 'white', 
                      borderRadius: '50%', 
                      width: '24px', 
                      height: '24px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      marginRight: '10px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      🏷️
                    </span>
                    <h6 style={{ margin: '0', fontWeight: 'bold', color: '#007bff', fontSize: '16px' }}>
                     Valores de atributos del producto 
                    </h6>
                  </div>

                  {/* Atributos de Odoo agregados - Solo para la primera variante */}
                  {variants.length === 0 && currentVariant.odooAttributes.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h6 style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#333' }}>
                        Atributos Agregados:
                      </h6>
                      {currentVariant.odooAttributes.map((attr, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px 15px',
                          backgroundColor: 'white',
                          border: '1px solid #dee2e6',
                          borderRadius: '8px', 
                          marginBottom: '8px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}>
                          <div>
                            <span style={{ fontWeight: 'bold', color: '#495057' }}>
                              {attr.attributeName}:
                            </span>
                            <span style={{ color: '#6c757d', marginLeft: '8px' }}>
                              {attr.valueName}
                            </span>
                          </div>
                          <button
                            onClick={() => removeOdooAttribute(index)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: 'bold'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Sección: Seleccionar Valores para cada Atributo Global (por variante) */}
                  {globalAttributes.length > 0 && (
                    <div style={{ 
                      backgroundColor: 'white', 
                      padding: '20px', 
                      borderRadius: '10px',
                      border: '1px solid #dee2e6',
                      boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                      marginBottom: '20px'
                    }}>
                      <h6 style={{ margin: '0 0 15px 0', color: '#000000', fontWeight: 'bold' }}>
                        Seleccionar valores para esta variante:
                      </h6>
                      
                      {globalAttributes.map((globalAttr) => {
                        const existingAttr = currentVariant.odooAttributes.find(attr => attr.attributeId === globalAttr.attributeId);
                        const attrId = globalAttr.attributeId;
                        
                        return (
                          <div key={attrId} style={{ marginBottom: '20px' }}>
                            <label style={{ 
                              display: 'block', 
                              fontSize: '14px', 
                              fontWeight: 'bold', 
                              marginBottom: '8px', 
                              color: '#333' 
                            }}>
                              {globalAttr.attributeName}: {existingAttr && <span style={{ color: '#28a745', fontSize: '12px' }}>✓</span>}
                            </label>
                            
                            {existingAttr ? (
                              // Mostrar valor seleccionado
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '12px',
                                backgroundColor: '#e8f5e8',
                                border: '1px solid #d4edda',
                                borderRadius: '8px'
                              }}>
                                <span style={{
                                  flex: 1,
                                  fontSize: '14px',
                                  color: '#155724',
                                  fontWeight: 'bold'
                                }}>
                                  {existingAttr.valueName}
                                </span>
                                <button
                                  onClick={() => {
                                    setCurrentVariant(prev => ({
                                      ...prev,
                                      odooAttributes: prev.odooAttributes.filter(attr => attr.attributeId !== attrId)
                                    }));
                                  }}
                                  style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  Cambiar
                                </button>
                              </div>
                            ) : (
                              // Buscador de valores
                              <div>
                                <input
                                  type="text"
                                  placeholder={`Buscar valor para ${globalAttr.attributeName}...`}
                                  value={attributeSearchTexts[attrId] || ''}
                                  onChange={async (e) => {
                                    const searchTerm = e.target.value;
                                    setAttributeSearchTexts(prev => ({
                                      ...prev,
                                      [attrId]: searchTerm
                                    }));
                                    if (searchTerm.trim()) {
                                      const response = await getOdooValueAttributes(entId, attrId, searchTerm);
                                      const values = response?.attributeValues || [];
                                      setAttributeValuesMap(prev => ({
                                        ...prev,
                                        [attrId]: values
                                      }));
                                    } else {
                                      const response = await getOdooValueAttributes(entId, attrId, '');
                                      const values = response?.attributeValues || [];
                                      setAttributeValuesMap(prev => ({
                                        ...prev,
                                        [attrId]: values
                                      }));
                                    }
                                  }}
                                  onFocus={async () => {
                                    setFocusedAttributeInput(attrId);
                                    setSelectedAttribute(attrId);
                                    // Cargar valores si no están cargados
                                    if (!attributeValuesMap[attrId] || attributeValuesMap[attrId].length === 0) {
                                      const response = await getOdooValueAttributes(entId, attrId, '');
                                      const values = response?.attributeValues || [];
                                      setAttributeValuesMap(prev => ({
                                        ...prev,
                                        [attrId]: values
                                      }));
                                    }
                                  }}
                                  onBlur={() => {
                                    setTimeout(() => {
                                      const activeElement = document.activeElement;
                                      // Verificar si se está haciendo click en la lista
                                      const isClickingOnList = activeElement && (
                                        activeElement.closest('[style*="maxHeight: 200px"]') ||
                                        activeElement.closest('[style*="overflowY: auto"]')
                                      );
                                      // Verificar si se está haciendo click en otro input de atributo
                                      const isClickingOnAnotherInput = activeElement && 
                                        activeElement.tagName === 'INPUT' &&
                                        activeElement.placeholder &&
                                        activeElement.placeholder.includes('Buscar valor para');
                                      
                                      // Solo cerrar si no se está haciendo click en la lista ni en otro input
                                      if (!isClickingOnList && !isClickingOnAnotherInput) {
                                        // Solo cerrar si este input sigue siendo el enfocado
                                        if (focusedAttributeInput === attrId) {
                                          setFocusedAttributeInput(null);
                                        }
                                      }
                                    }, 200);
                                  }}
                                  style={{ 
                                    width: '100%', 
                                    padding: '12px', 
                                    fontSize: '14px', 
                                    border: '1px solid #ddd', 
                                    borderRadius: '8px',
                                    backgroundColor: 'white',
                                    marginBottom: '10px'
                                  }}
                                />
                                
                                {/* Lista de valores */}
                                {focusedAttributeInput === attrId && attributeValuesMap[attrId] && attributeValuesMap[attrId].length > 0 && (
                                  <div style={{
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    backgroundColor: 'white',
                                    marginBottom: '10px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}>
                                    {attributeValuesMap[attrId].map((value) => (
                                      <div
                                        key={value._id || value.id}
                                        onMouseDown={(e) => {
                                          e.preventDefault(); // Prevenir que el blur se ejecute antes del click
                                        }}
                                        onClick={() => {
                                          const selectedAttr = odooAttributes.find(a => a.id.toString() === attrId);
                                          const newAttribute = {
                                            attributeId: attrId,
                                            attributeName: selectedAttr?.name || globalAttr.attributeName,
                                            valueId: value.id,
                                            valueName: value.name
                                          };
                                          
                                          setCurrentVariant(prev => ({
                                            ...prev,
                                            odooAttributes: [
                                              ...prev.odooAttributes.filter(attr => attr.attributeId !== attrId),
                                              newAttribute
                                            ]
                                          }));
                                          
                                          setFocusedAttributeInput(null);
                                          setAttributeSearchTexts(prev => {
                                            const newTexts = { ...prev };
                                            delete newTexts[attrId];
                                            return newTexts;
                                          });
                                          
                                          setTimeout(() => {
                                            const input = document.querySelector(`input[placeholder="Buscar valor para ${globalAttr.attributeName}..."]`);
                                            if (input) {
                                              input.blur();
                                            }
                                          }, 50);
                                        }}
                                        style={{
                                          padding: '12px',
                                          cursor: 'pointer',
                                          borderBottom: '1px solid #f0f0f0',
                                          transition: 'background-color 0.2s ease',
                                          backgroundColor: 'white',
                                          fontSize: '14px'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.backgroundColor = '#f8f9fa';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor = 'white';
                                        }}
                                      >
                                        {value.name}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  
                  {/* Mostrar atributos obligatorios si hay variantes previas
                  {variants.length > 0 && requiredAttributes.length > 0 && (
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#fff3cd', 
                      border: '1px solid #ffeaa7', 
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: '#856404',
                      marginTop: '15px'
                    }}>
                      <strong>⚠️ Atributos Obligatorios:</strong> Selecciona valores para los mismos atributos de la primera variante:
                      <div style={{ marginTop: '8px' }}>
                        {requiredAttributes.map(attrId => {
                          const attr = odooAttributes.find(a => a.id.toString() === attrId);
                          const existingAttribute = currentVariant.odooAttributes.find(existingAttr => existingAttr.attributeId === attrId);
                          return (
                            <span key={attrId} style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              backgroundColor: existingAttribute ? '#d4edda' : '#f8f9fa',
                              border: '1px solid',
                              borderColor: existingAttribute ? '#c3e6cb' : '#dee2e6',
                              borderRadius: '4px',
                              margin: '2px',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              color: existingAttribute ? '#155724' : '#495057'
                            }}>
                              {attr?.name || attrId} {existingAttribute && `✓`}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )} */}
                  
                </div>

                {/* Sección 2: Formulario de detalles de la variante */}
                <div style={{ 
                    backgroundColor: '#fff5f5', 
                    border: '2px solid #fed7d7', 
                    borderRadius: '12px', 
                    padding: '20px',
                    marginBottom: '20px',
                    boxShadow: '0 2px 8px rgba(220, 53, 69, 0.1)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '15px',
                      paddingBottom: '10px',
                      borderBottom: '2px solid #fed7d7'
                    }}>
                      <span style={{ 
                        backgroundColor: '#dc3545', 
                        color: 'white', 
                        borderRadius: '50%', 
                        width: '24px', 
                        height: '24px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        marginRight: '10px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        📋
                      </span>
                      <h6 style={{ margin: '0', fontWeight: 'bold', color: '#dc3545', fontSize: '16px' }}>
                        Detalles de la Variante <span style={{ color: '#dc3545' }}>*</span>
                      </h6>
                    </div>
                  
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '20px', 
                    borderRadius: '10px',
                    border: '1px solid #fed7d7',
                    boxShadow: 'inset 0 1px 3px rgba(220, 53, 69, 0.1)'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                          Peso (kg): <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        <input 
                          type="number"
                          min="0"
                          step="0.01"
                          value={currentVariant.peso}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            if (value >= 0 || e.target.value === '') {
                              updateCurrentVariant('peso', e.target.value);
                            }
                          }}
                          onBlur={(e) => {
                            if (e.target.value && parseFloat(e.target.value) < 0) {
                              updateCurrentVariant('peso', '');
                              alert('El peso no puede ser negativo');
                            }
                          }}
                          style={{ 
                            width: '100%', 
                            padding: '12px', 
                            fontSize: '14px', 
                            border: '2px solid #fed7d7', 
                            borderRadius: '8px',
                            backgroundColor: 'white',
                            transition: 'border-color 0.3s ease'
                          }}
                          placeholder="Ej: 0.5 kg" 
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                          Volumen (cm<sup>3</sup>): <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        <input 
                          type="number"
                          min="0"
                          step="0.01"
                          value={currentVariant.volumen}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            if (value >= 0 || e.target.value === '') {
                              updateCurrentVariant('volumen', e.target.value);
                              setIsVolumeManuallyEdited(true); // Marcar como editado manualmente
                            }
                          }}
                          onBlur={(e) => {
                            if (e.target.value && parseFloat(e.target.value) < 0) {
                              updateCurrentVariant('volumen', '');
                              alert('El volumen no puede ser negativo');
                            }
                          }}
                          style={{ 
                            width: '100%', 
                            padding: '12px', 
                            fontSize: '14px', 
                            border: '2px solid #fed7d7', 
                            borderRadius: '8px',
                            backgroundColor: 'white',
                            transition: 'border-color 0.3s ease'
                          }}
                          placeholder="Ej: 1000" 
                        />
                      </div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                          SKU: <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        <input 
                          type="text"
                          value={currentVariant.sku}
                          onChange={(e) => updateCurrentVariant('sku', e.target.value)}
                          style={{ 
                            width: '100%', 
                            padding: '12px', 
                            fontSize: '14px', 
                            border: '2px solid #fed7d7', 
                            borderRadius: '8px',
                            backgroundColor: 'white',
                            transition: 'border-color 0.3s ease'
                          }}
                          placeholder="Código SKU de la variante" 
                        />
                      </div>
                                              <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                          Código de Barras:
                        </label>
                        <input 
                          type="text"
                          value={currentVariant.barcode}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Solo permitir números
                            if (value === '' || /^\d+$/.test(value)) {
                              updateCurrentVariant('barcode', value);
                            }
                          }}
                          minLength="8"
                          maxLength="13"
                          style={{ 
                            width: '100%', 
                            padding: '12px', 
                            fontSize: '14px', 
                            border: '2px solid #fed7d7', 
                            borderRadius: '8px',
                            backgroundColor: 'white',
                            transition: 'border-color 0.3s ease'
                          }}
                          placeholder="Solo números (mín. 8 dígitos)" 
                        />
                        {currentVariant.barcode && currentVariant.barcode.length < 8 && (
                          <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', fontWeight: 'bold' }}>
                            ⚠️ El código de barras debe tener al menos 8 dígitos
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
            
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                      Imagen Principal de la Variante: <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <div style={{ 
                      padding: '8px 12px', 
                      backgroundColor: '#e3f2fd', 
                      border: '1px solid #bbdefb', 
                      borderRadius: '4px',
                      fontSize: '11px',
                      color: '#1976d2',
                      marginBottom: '8px'
                    }}>
                      <strong>📋 Requisitos:</strong> Imagen cuadrada, máximo 1500x1500px, 1MB
                    </div>
              <div 
                style={{ 
                        border: '2px dashed #ccc', 
                        borderRadius: '6px', 
                        padding: '20px',
                        minHeight: '80px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  marginBottom: '5px',
                        cursor: 'pointer',
                        backgroundColor: 'white'
                }}
                onClick={() => document.getElementById('variant-modal-mainImageUpload').click()}
              >
                      {currentVariant.mainImage ? (
                  <img 
                          src={currentVariant.mainImage} 
                    alt="Preview Variante" 
                          style={{ maxWidth: '60px', maxHeight: '60px', objectFit: 'contain' }}
                  />
                ) : (
                        <div style={{ textAlign: 'center' }}>
                          <span style={{ fontSize: '24px', color: '#666' }}>📷</span>
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                            Click para subir imagen principal
                          </div>
                        </div>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                      onChange={handleMainImageUpload}
                style={{ display: 'none' }}
                id="variant-modal-mainImageUpload"
              />
            </div>

                  {/* Checkbox para mostrar/ocultar imágenes adicionales */}
                  <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px dashed #ccc' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px',
                      marginBottom: '15px'
                    }}>
                      <input
                        type="checkbox"
                        id="variant-modal-showAdditionalImages"
                        checked={showAdditionalImages}
                        onChange={(e) => setShowAdditionalImages(e.target.checked)}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer'
                        }}
                      />
                      <label 
                        style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: '#333',
                          margin: '0'
                        }}
                      >
                        ¿Quieres agregar imágenes adicionales?
                      </label>
                    </div>
                    {showAdditionalImages && (
                      <p style={{ margin: '0 0 15px 0', color: '#666', fontSize: '11px', fontStyle: 'italic' }}>
                        Los campos marcados con <span style={{ color: '#dc3545' }}>*</span> son obligatorios cuando se activa esta opción.
                      </p>
                    )}

                    {/* Sección para imágenes adicionales - Solo se muestra si el checkbox está marcado */}
                    {showAdditionalImages && (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <h6 style={{ margin: '0', fontWeight: 'bold', color: '#333' }}>
                            Imágenes Adicionales: <span style={{ color: '#dc3545' }}>*</span>
                          </h6>
                          <span style={{ 
                            fontSize: '12px', 
                            color: currentVariant.additionalImages.length >= 5 ? '#dc3545' : '#28a745',
                            fontWeight: 'bold'
                          }}>
                            {currentVariant.additionalImages.length}/5
                          </span>
                        </div>

                        <div style={{ 
                          padding: '8px 12px', 
                          backgroundColor: '#fff3cd', 
                          border: '1px solid #ffeaa7', 
                          borderRadius: '4px',
                          fontSize: '11px',
                          color: '#856404',
                          marginBottom: '10px'
                        }}>
                          <strong>📋 Requisitos:</strong> Imágenes cuadradas, máximo 1500x1500px, 1MB cada una. Máximo 5 imágenes.
                        </div>
                        
                        {currentVariant.additionalImages.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                            {currentVariant.additionalImages.map((image, index) => (
                              <div key={index} style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '4px', overflow: 'hidden' }}>
                                <img src={image} alt={`Additional ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                <button
                                  onClick={() => removeAdditionalImage(index)}
                                  style={{
                                    position: 'absolute',
                                    top: '2px',
                                    right: '2px',
                                    background: 'rgba(255, 255, 255, 0.7)',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    color: '#dc3545'
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {currentVariant.additionalImages.length < 5 && (
                          <div 
                            style={{ 
                              border: `2px dashed ${showAdditionalImages && currentVariant.additionalImages.length === 0 ? '#dc3545' : '#ccc'}`, 
                              borderRadius: '6px', 
                              padding: '20px',
                              minHeight: '80px', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              marginBottom: '5px',
                              cursor: 'pointer',
                              backgroundColor: 'white',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={() => document.getElementById('variant-modal-additionalImageUpload').click()}
                          >
                            <div style={{ textAlign: 'center' }}>
                              <span style={{ fontSize: '24px', color: '#666' }}>📷</span>
                              <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Click para subir imágenes adicionales
                              </div>
                              <div style={{ fontSize: '10px', color: '#999', marginTop: '3px' }}>
                                {5 - currentVariant.additionalImages.length} espacios disponibles
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {currentVariant.additionalImages.length >= 5 && (
                          <div style={{ 
                            padding: '12px', 
                            backgroundColor: '#f8d7da', 
                            border: '1px solid #f5c6cb', 
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: '#721c24',
                            textAlign: 'center'
                          }}>
                            <strong>⚠️ Límite alcanzado:</strong> Ya tienes 5 imágenes adicionales
                          </div>
                        )}
                        
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAdditionalImagesUpload}
                          style={{ display: 'none' }}
                          id="variant-modal-additionalImageUpload"
                          multiple // Permite seleccionar múltiples archivos
                        />
                      </div>
                    )}
                  </div>
                </div>
                {/* Botón de guardar variante */}
                {globalAttributes.length > 0 && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                      {editingVariantId && (
                        <button
                          onClick={cancelEdit}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}
                        >
                          Cancelar
                        </button>
                      )}
                      <button
                        onClick={saveCurrentVariant}
                        disabled={
                          !currentVariant.peso || 
                          !currentVariant.volumen || 
                          !currentVariant.sku || 
                          !currentVariant.mainImage ||
                          (showAdditionalImages && currentVariant.additionalImages.length === 0) ||
                          !globalAttributes.every(globalAttr => 
                            currentVariant.odooAttributes.some(attr => attr.attributeId === globalAttr.attributeId)
                          )
                        }
                        style={{
                          padding: '10px 20px',
                          backgroundColor: (
                            currentVariant.peso && 
                            currentVariant.volumen && 
                            currentVariant.sku && 
                            currentVariant.mainImage &&
                            (!showAdditionalImages || currentVariant.additionalImages.length > 0) &&
                            globalAttributes.every(globalAttr => 
                              currentVariant.odooAttributes.some(attr => attr.attributeId === globalAttr.attributeId)
                            )
                          ) ? (editingVariantId ? '#ffc107' : '#28a745') : '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: (
                            currentVariant.peso && 
                            currentVariant.volumen && 
                            currentVariant.sku && 
                            currentVariant.mainImage &&
                            (!showAdditionalImages || currentVariant.additionalImages.length > 0) &&
                            globalAttributes.every(globalAttr => 
                              currentVariant.odooAttributes.some(attr => attr.attributeId === globalAttr.attributeId)
                            )
                          ) ? 'pointer' : 'not-allowed',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}
                      >
                        {editingVariantId ? 'Actualizar Variante' : 'Guardar Variante'}
                      </button>
                    </div>
                    {(!currentVariant.peso || !currentVariant.volumen || !currentVariant.sku || !currentVariant.mainImage) && (
                      <div style={{ 
                        marginTop: '8px', 
                        fontSize: '12px', 
                        color: '#dc3545',
                        fontStyle: 'italic'
                      }}>
                        * Todos los campos son obligatorios
                      </div>
                    )}
                    {showAdditionalImages && currentVariant.additionalImages.length === 0 && (
                      <div style={{ 
                        marginTop: '8px', 
                        fontSize: '12px', 
                        color: '#dc3545',
                        fontStyle: 'italic'
                      }}>
                        * Las imágenes adicionales son obligatorias cuando el checkbox está activo
                      </div>
                    )}
                    {globalAttributes.length > 0 && 
                     !globalAttributes.every(globalAttr => 
                       currentVariant.odooAttributes.some(attr => attr.attributeId === globalAttr.attributeId)
                     ) && (
                      <div style={{ 
                        marginTop: '8px', 
                        fontSize: '12px', 
                        color: '#dc3545',
                        fontStyle: 'italic'
                      }}>
                        * Debes seleccionar un valor para todos los atributos: {globalAttributes
                          .filter(globalAttr => !currentVariant.odooAttributes.some(attr => attr.attributeId === globalAttr.attributeId))
                          .map(attr => attr.attributeName)
                          .join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          


          {/* Botones del Modal */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '10px', 
            marginTop: '20px',
            paddingTop: '15px',
            borderTop: '1px solid #dee2e6'
          }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={() => onSave(variants)}
              disabled={variants.length === 0}
              style={{
                padding: '8px 16px',
                backgroundColor: variants.length > 0 ? '#007bff' : '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: variants.length > 0 ? 'pointer' : 'not-allowed'
              }}
            >
              Guardar Variantes ({variants.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariantModal; 